import React, { useRef, useState } from "react";
import { X, Plus, File, FileCode } from "lucide-react";

// Map file extension to a color for the icon
function extColor(path) {
  const ext = (path || "").split(".").pop().toLowerCase();
  const map = {
    py: "text-sky-400", js: "text-yellow-400", ts: "text-blue-400",
    jsx: "text-cyan-400", tsx: "text-blue-300", html: "text-orange-400",
    css: "text-purple-400", json: "text-green-400", md: "text-slate-400",
    go: "text-teal-400", java: "text-orange-500", c: "text-blue-500",
    cpp: "text-indigo-400", rb: "text-red-400", php: "text-violet-400",
    sh: "text-emerald-400", lua: "text-blue-300", sql: "text-amber-400",
  };
  return map[ext] || "text-ide-mute";
}

function FileIcon({ path }) {
  const cls = extColor(path);
  return <FileCode className={`h-3.5 w-3.5 flex-none ${cls}`} />;
}

export default function FileTabs({ files, activeFile, onSwitch, onAdd, onDelete, language, langInfo }) {
  const scrollRef = useRef(null);
  const [showNewFile, setShowNewFile] = useState(false);
  const [newFileName, setNewFileName] = useState("");

  const defaultExt = langInfo?.file_ext || ".txt";

  function openNewFile() {
    setNewFileName(`utils${defaultExt}`);
    setShowNewFile(true);
  }

  function submitNewFile(e) {
    e.preventDefault();
    const name = newFileName.trim();
    if (!name) return;
    // Prevent duplicates
    if (files.some((f) => f.path === name)) {
      setNewFileName(name + "_2");
      return;
    }
    onAdd?.(name);
    setShowNewFile(false);
    setNewFileName("");
  }

  return (
    <>
      <div className="flex items-stretch border-b border-ide-border bg-ide-panel flex-none select-none" style={{ minHeight: 34 }}>
        {/* Scrollable tab list */}
        <div ref={scrollRef} className="flex items-stretch flex-1 overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          {files.map((f) => {
            const isActive = f.path === activeFile;
            const name = f.path.split("/").pop();
            return (
              <div
                key={f.path}
                data-testid={`file-tab-${f.path}`}
                className={
                  "group relative flex items-center gap-1.5 px-3 py-1.5 cursor-pointer transition-all duration-100 border-r border-ide-border flex-none max-w-[160px] " +
                  (isActive
                    ? "bg-ide-panel2 text-ide-text"
                    : "text-ide-mute hover:text-ide-text hover:bg-ide-surface")
                }
                onClick={() => onSwitch?.(f.path)}
                title={f.path}
              >
                <FileIcon path={f.path} />
                <span className="text-[12px] truncate font-mono leading-none">{name}</span>
                {f.dirty && (
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 flex-none" title="unsaved changes" />
                )}
                {files.length > 1 && (
                  <button
                    data-testid={`close-tab-${f.path}`}
                    className="flex-none ml-0.5 opacity-0 group-hover:opacity-100 hover:text-ide-danger rounded p-0.5 hover:bg-ide-surface transition-opacity"
                    onClick={(e) => { e.stopPropagation(); onDelete?.(f.path); }}
                    title={`Close ${name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-ide-accent to-ide-accent2" />
                )}
              </div>
            );
          })}
        </div>

        {/* Add file button */}
        <button
          data-testid="add-file-btn"
          onClick={openNewFile}
          className="flex-none flex items-center justify-center px-2.5 text-ide-mute hover:text-ide-text hover:bg-ide-surface transition-colors border-l border-ide-border"
          title="Add new file"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Inline "new file" input (shows below tabs) */}
      {showNewFile && (
        <form
          onSubmit={submitNewFile}
          className="flex items-center gap-2 px-3 py-1.5 bg-ide-surface border-b border-ide-border flex-none"
        >
          <File className="h-3.5 w-3.5 text-ide-mute flex-none" />
          <input
            autoFocus
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setShowNewFile(false); }}
            className="flex-1 bg-transparent text-sm font-mono text-ide-text border-b border-ide-accent/50 focus:outline-none pb-0.5 placeholder:text-ide-mute"
            placeholder={`filename${defaultExt}`}
            data-testid="new-file-input"
            spellCheck={false}
          />
          <button
            type="submit"
            className="text-[11px] px-2 py-0.5 rounded bg-ide-accent/20 text-ide-accent border border-ide-accent/40 hover:bg-ide-accent/30 transition-colors"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setShowNewFile(false)}
            className="text-ide-mute hover:text-ide-text"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </form>
      )}
    </>
  );
}
